import * as React from 'react'

import { clsx } from 'clsx'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import {
  ArrowDownIcon,
  ArrowUpIcon,
  Button,
  Kbd,
  Tooltip,
} from '@buffer-mono/popcorn'

import styles from './NewItemsAvailableButton.module.css'

import type { JSX } from 'react'

type NewItemsAvailableButtonProps = {
  /** Whether the pill is currently visible. */
  show: boolean
  /** While true, the label is swapped for a spinner. */
  isFetching: boolean
  /** Pill copy, e.g. "New comments available". */
  label: string
  onClick: () => void
  /**
   * Where the pill sits.
   *
   * - `page` centres it over the full page.
   * - `commentsColumn` centres it over the comments column, which the by-post
   *   layout shifts to the right of the page centre.
   * - `postsPanel` centres it over the element that contains it, and narrows
   *   the button so its copy fits inside the posts sidepanel.
   */
  placement?: 'page' | 'commentsColumn' | 'postsPanel'
  /**
   * Whether the by-post posts sidepanel is collapsed. Only meaningful with the
   * `commentsColumn` placement, whose offset depends on the panel's width.
   */
  panelCollapsed?: boolean
  /**
   * Point the arrow down instead of up, for oldest-first sort where the new
   * items land at the bottom of the list.
   */
  arrowDown?: boolean
}

/**
 * Presentational pill rendered above a Community list when new items have been
 * ingested. Shared by the comments and mentions variants: it owns the
 * animation, spinner, styling, and the jump shortcut, while each caller owns
 * its own detection (which Pusher event) and refetch (which query).
 */
export const NewItemsAvailableButton = ({
  show,
  isFetching,
  label,
  onClick,
  placement = 'page',
  panelCollapsed = false,
  arrowDown = false,
}: NewItemsAvailableButtonProps): JSX.Element => {
  // While the pill is shown, Cmd/Ctrl+J loads the new items, so keyboard users
  // can jump to them without the mouse. Mirrors the tooltip's shortcut hint.
  React.useEffect(() => {
    if (!show || isFetching) return
    const handleKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault()
        onClick()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [show, isFetching, onClick])

  // The panel pill spans its container, so it sits where it is laid out. The
  // other placements are pinned to a midpoint and pulled back by half their
  // own width to centre on it.
  const centeringShift = placement === 'postsPanel' ? '0%' : '-50%'

  return (
    <AnimatePresence>
      {show && (
        <m.div
          className={clsx(
            styles.buttonContainer,
            placement === 'commentsColumn' && styles.commentsColumn,
            placement === 'commentsColumn' &&
              panelCollapsed &&
              styles.panelCollapsed,
            placement === 'postsPanel' && styles.postsPanel,
          )}
          initial={{ opacity: 0, y: 8, x: centeringShift }}
          animate={{ opacity: 1, y: 0, x: centeringShift }}
          exit={{ opacity: 0, y: 8, x: centeringShift }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <Tooltip
            side="bottom"
            sideOffset={8}
            arrow
            content={
              <>
                Jump <Kbd color="inverted">mod</Kbd>{' '}
                <Kbd color="inverted">J</Kbd>
              </>
            }
          >
            <Button
              as="button"
              size={placement === 'postsPanel' ? 'small' : 'medium'}
              variant="primary"
              loading={isFetching}
              onClick={onClick}
              className={clsx(
                styles.button,
                placement === 'postsPanel' && styles.buttonInPanel,
              )}
            >
              {!isFetching && (arrowDown ? <ArrowDownIcon /> : <ArrowUpIcon />)}
              {label}
            </Button>
          </Tooltip>
        </m.div>
      )}
    </AnimatePresence>
  )
}
